export function addItemToLocalCart(
  cartData = {},
  product,
  quantityToAdd = null
) {
  const productId = product.product_id;
  const unitPrice = product.price || 0;
  // Use quantityToAdd if provided, otherwise use minimum_quantity for initial add
  const newQty =
    quantityToAdd !== null
      ? Number(quantityToAdd)
      : Number(product.minimum_quantity || 1);

  // Sanitize product data to prevent XSS and ensure data integrity
  const sanitizedProduct = {
    sku_code: String(productId).replace(/[<>\"']/g, ""),
    image: String(product.image || "").replace(/[<>\"']/g, ""),
    name: String(product.name || "").replace(/[<>\"']/g, ""),
    price: Number(unitPrice) || 0,
    weight: Number(product.weight) || 0,
    weight_symbol: String(product.weight_symbol || "lbs").replace(
      /[<>\"']/g,
      ""
    ),
    short_description: String(product.short_description || "").replace(
      /[<>\"']/g,
      ""
    ),
    cart_preference:
      typeof product.cart_preference === "object"
        ? product.cart_preference
        : {},
    // Subscription fields
    enable_subscription: product.enable_subscription || false,
    subscription_frequency: product.subscription_frequency || "",
    subscription_preferences: product.subscription_preferences || [],
    subscription_cuts: product.subscription_cuts || null,
    subscription_custom_notes: product.subscription_custom_notes || "",
  };

  const newCart = { ...cartData };
  if (!newCart.item_info) newCart.item_info = {};

  const existingItem = newCart.item_info[productId];

  if (existingItem) {
    existingItem.product_quantity =
      Number(existingItem.product_quantity) + Number(newQty);
    existingItem.product_total = parseFloat(
      (existingItem.product_quantity * sanitizedProduct.price).toFixed(2)
    );
    // Update subscription details if they exist in the new add
    if (sanitizedProduct.enable_subscription) {
      existingItem.enable_subscription = sanitizedProduct.enable_subscription;
      existingItem.subscription_frequency =
        sanitizedProduct.subscription_frequency;
      existingItem.subscription_preferences =
        sanitizedProduct.subscription_preferences;
      existingItem.subscription_cuts = sanitizedProduct.subscription_cuts;
      existingItem.subscription_custom_notes =
        sanitizedProduct.subscription_custom_notes;
    }
  } else {
    newCart.item_info[productId] = {
      product_id: productId,
      product_image: sanitizedProduct.image,
      product_name: sanitizedProduct.name,
      product_price: sanitizedProduct.price,
      product_quantity: newQty,
      product_total: parseFloat((sanitizedProduct.price * newQty).toFixed(2)),
      product_instruction: "",
      weight: sanitizedProduct.weight,
      weight_symbol: sanitizedProduct.weight_symbol,
      short_description: sanitizedProduct.short_description,
      product_preference: sanitizedProduct.cart_preference,
      // Subscription fields
      enable_subscription: sanitizedProduct.enable_subscription,
      subscription_frequency: sanitizedProduct.subscription_frequency,
      subscription_preferences: sanitizedProduct.subscription_preferences,
      subscription_cuts: sanitizedProduct.subscription_cuts,
      subscription_custom_notes: sanitizedProduct.subscription_custom_notes,
    };
  }

  const allItems = Object.values(newCart.item_info);
  newCart.cart_total_quantity = allItems.reduce(
    (sum, item) => sum + item.product_quantity,
    0
  );
  newCart.formatted_final_order_total = parseFloat(
    allItems.reduce((sum, item) => sum + item.product_total, 0).toFixed(2)
  );
  newCart.item_total = parseFloat(
    allItems.reduce((sum, item) => sum + item.product_total, 0).toFixed(2)
  );
  // newCart.formatted_final_order_total = `$${newCart.order_total.toFixed(2)}`;
  newCart.formatted_item_total = `$${newCart.formatted_final_order_total.toFixed(
    2
  )}`;
  newCart.formatted_shipping_handling = `$0.00`;
  newCart.formatted_discount_total = `$0.00`;
  // Add timestamp for data integrity verification
  newCart.last_updated = Date.now();
  newCart.data_version = "1.0";

  return newCart;
}

export function removeQtyFromLocalCart(
  cartData = {},
  productId,
  removeQty = 1
) {
  const newCart = { ...cartData };
  if (!newCart.item_info || !newCart.item_info[productId]) {
    return newCart; // nothing to remove
  }

  const item = newCart.item_info[productId];
  const newQty = item.product_quantity - removeQty;

  if (newQty > 0) {
    item.product_quantity = newQty;
    item.product_total = parseFloat((newQty * item.product_price).toFixed(2));
  } else {
    // Remove the item if quantity is 0 or below
    delete newCart.item_info[productId];
  }

  const allItems = Object.values(newCart.item_info);

  if (allItems.length > 0) {
    newCart.cart_total_quantity = allItems.reduce(
      (sum, item) => sum + item.product_quantity,
      0
    );
    newCart.formatted_final_order_total = parseFloat(
      allItems.reduce((sum, item) => sum + item.product_total, 0).toFixed(2)
    );
    newCart.formatted_order_total = `$${newCart.formatted_final_order_total.toFixed(
      2
    )}`;
  } else {
    // If no items remain, clear cart
    return {};
  }

  return newCart;
}

export function updateCartPreference(cartData = {}, payload) {
  const productId = payload.product_id;
  const item = cartData?.item_info?.[productId];

  if (!item) return cartData;

  const { preference, preference_price, cuts, cut_price, custom_notes } =
    payload;

  // Ensure structure
  // if (!item.product_preference) {
  //     item.product_preference = { preference: [], cut: null, custom_note: '' };
  // }

  // === Update preference ===
  if (typeof preference !== "undefined") {
    const prefArray = item?.product_preference?.preference || [];
    const index = prefArray.findIndex((p) => p.label === preference);

    if (index !== -1) {
      prefArray.splice(index, 1); // toggle off
    } else {
      prefArray.push({ label: preference, price: preference_price });
    }

    // Ensure product_preference exists before assignment
    if (item && item.product_preference) {
      item.product_preference.preference = prefArray;
    }
  }

  // === Update cut ===
  if (typeof cuts !== "undefined") {
    item.product_preference.cut = { label: cuts, price: cut_price };
  }

  // === Update custom note ===
  if (typeof custom_notes !== "undefined") {
    item.product_preference.custom_note = custom_notes;
  }

  // === Recalculate preference total ===
  const prefTotal = (item?.product_preference?.preference || []).reduce(
    (sum, p) => sum + Number(p.price || 0),
    0
  );
  const cutTotal = Number(item?.product_preference?.cut?.price || 0);

  item.preferences_total = parseFloat((prefTotal + cutTotal).toFixed(2));

  // === Update product total ===
  const baseTotal = item.product_quantity * item.product_price;
  item.product_total = parseFloat(
    (baseTotal + item.preferences_total).toFixed(2)
  );

  // ✅ Recalculate full cart totals
  const allItems = Object.values(cartData.item_info || {});
  cartData.cart_total_quantity = allItems.reduce(
    (sum, item) => sum + item.product_quantity,
    0
  );
  cartData.formatted_final_order_total = parseFloat(
    allItems.reduce((sum, item) => sum + item.product_total, 0).toFixed(2)
  );
  cartData.formatted_order_total = `$${cartData.formatted_final_order_total.toFixed(
    2
  )}`;

  return { ...cartData };
}
